import { useState } from 'react';

import type { RegionNode } from '../../../api/regions';
import {
  createVenue,
  enrichVenueFromDomain,
  VenueValidationError,
  type VenueDetail,
} from '../../../api/venues';
import { soleRegion } from '../workshops/create/workshopCreateRegions';

export type Stage = 'active' | 'prospect';
export type EnrichState = 'idle' | 'fetching' | 'done' | 'failed';

export interface VenueCreateFields {
  name: string;
  domain: string;
  addressLine1: string;
  city: string;
  state: string;
  zipCode: string;
}

// Which step each create field lives on — used to jump to the first step
// carrying a server-side validation error.
const FIELD_STEP: Record<string, number> = {
  name: 0,
  domain: 0,
  addressLine1: 1,
  city: 1,
  state: 1,
  zipCode: 1,
  regionId: 1,
};

export interface VenueCreateForm {
  step: number;
  setStep: (i: number) => void;
  stage: Stage;
  setStage: (s: Stage) => void;
  fields: VenueCreateFields;
  edit: (
    key: keyof VenueCreateFields,
  ) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  /** Set several fields at once, as typing into each would. */
  update: (patch: Partial<VenueCreateFields>) => void;
  /** 'suggested' if the field is an unreviewed enrichment value, 'error' if it
   *  failed validation, else undefined. */
  fieldState: (key: keyof VenueCreateFields) => 'error' | 'suggested' | undefined;
  fieldError: (key: keyof VenueCreateFields) => string | undefined;
  /** The picked region, or the viewer's only region when they have just one.
   *  Empty means the server assigns one from the state. */
  regionId: string;
  setRegionId: (regionId: string) => void;
  regionError: string | undefined;
  enrich: EnrichState;
  pull: () => void;
  submit: () => void;
  submitting: boolean;
  submitError: string | null;
}

interface SubmitArgs {
  stage: Stage;
  fields: VenueCreateFields;
  regionId: string;
  onCreated: (venue: VenueDetail) => void;
  setSubmitting: (b: boolean) => void;
  setSubmitError: (s: string | null) => void;
  setFieldErrors: (m: Record<string, string>) => void;
  setStep: (i: number) => void;
}

/** Build the create payload, POST it, and route validation errors back to the
 *  right field + step. Extracted from the hook to keep it under the line cap. */
async function submitVenue(args: SubmitArgs): Promise<void> {
  const {
    stage,
    fields,
    regionId,
    onCreated,
    setSubmitting,
    setSubmitError,
    setFieldErrors,
    setStep,
  } = args;
  setSubmitting(true);
  setSubmitError(null);
  setFieldErrors({});
  try {
    const venue = await createVenue({
      stage,
      name: fields.name,
      domain: fields.domain,
      ...(regionId ? { regionId } : {}),
      ...(stage === 'active'
        ? {
            addressLine1: fields.addressLine1,
            city: fields.city,
            state: fields.state,
            zipCode: fields.zipCode,
          }
        : {}),
    });
    onCreated(venue);
  } catch (err) {
    if (err instanceof VenueValidationError) {
      setFieldErrors(err.fieldErrors);
      const firstStep = Object.keys(err.fieldErrors)
        .map((f) => FIELD_STEP[f] ?? 0)
        .sort((a, b) => a - b)[0];
      if (firstStep !== undefined) setStep(firstStep);
    } else {
      setSubmitError((err as Error).message);
    }
  } finally {
    setSubmitting(false);
  }
}

interface EnrichArgs {
  setEnrich: (s: EnrichState) => void;
  setFields: React.Dispatch<React.SetStateAction<VenueCreateFields>>;
  setSuggested: (m: Record<string, boolean>) => void;
}

/** Fetch domain enrichment and fold the suggested fields in; degrade to manual
 *  entry on failure. Extracted from the hook to keep it under the line cap. */
async function runEnrich(
  domain: string,
  { setEnrich, setFields, setSuggested }: EnrichArgs,
): Promise<void> {
  setEnrich('fetching');
  try {
    const r = await enrichVenueFromDomain(domain);
    setFields((f) => ({
      ...f,
      addressLine1: r.addressLine1 ?? f.addressLine1,
      city: r.city ?? f.city,
      state: r.state ?? f.state,
      zipCode: r.zipCode ?? f.zipCode,
    }));
    setSuggested({
      addressLine1: r.addressLine1 !== undefined,
      city: r.city !== undefined,
      state: r.state !== undefined,
      zipCode: r.zipCode !== undefined,
    });
    setEnrich('done');
  } catch {
    // Enrichment unavailable (endpoint not shipped, or read failed).
    setEnrich('failed');
  }
}

/** All state + side effects for the venue create drawer, so the drawer
 *  component stays presentational (BON-158). */
export function useVenueCreateForm(
  onCreated: (venue: VenueDetail) => void,
  regions: RegionNode[],
): VenueCreateForm {
  const [step, setStep] = useState(0);
  const [stage, setStage] = useState<Stage>('active');
  const [enrich, setEnrich] = useState<EnrichState>('idle');
  const [fields, setFields] = useState<VenueCreateFields>({
    name: '',
    domain: '',
    addressLine1: '',
    city: '',
    state: 'OR',
    zipCode: '',
  });
  // Null until the user picks; '' is their explicit "Assign from state".
  const [pickedRegionId, setPickedRegionId] = useState<string | null>(null);
  const regionId = pickedRegionId ?? soleRegion(regions)?.id ?? '';
  const [suggested, setSuggested] = useState<Record<string, boolean>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const update = (patch: Partial<VenueCreateFields>) => {
    const keys = Object.keys(patch);
    setFields((f) => ({ ...f, ...patch }));
    // A manual edit clears the "suggested from domain" provenance + any error.
    setSuggested((s) => ({ ...s, ...Object.fromEntries(keys.map((k) => [k, false])) }));
    setFieldErrors((fe) => ({ ...fe, ...Object.fromEntries(keys.map((k) => [k, ''])) }));
  };
  const edit =
    (key: keyof VenueCreateFields) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      update({ [key]: e.target.value });

  const fieldState = (key: keyof VenueCreateFields): 'error' | 'suggested' | undefined =>
    fieldErrors[key] ? 'error' : suggested[key] ? 'suggested' : undefined;
  const fieldError = (key: keyof VenueCreateFields) => fieldErrors[key] || undefined;
  const setRegionId = (id: string) => {
    setPickedRegionId(id);
    setFieldErrors((fe) => (fe.regionId ? { ...fe, regionId: '' } : fe));
  };

  const pull = () => {
    if (enrich === 'fetching' || !fields.domain.trim()) return;
    void runEnrich(fields.domain.trim(), { setEnrich, setFields, setSuggested });
  };

  const submit = () =>
    submitVenue({
      stage,
      fields,
      regionId,
      onCreated,
      setSubmitting,
      setSubmitError,
      setFieldErrors,
      setStep,
    });

  return {
    step,
    setStep,
    stage,
    setStage,
    fields,
    edit,
    update,
    fieldState,
    fieldError,
    regionId,
    setRegionId,
    regionError: fieldErrors.regionId || undefined,
    enrich,
    pull,
    submit,
    submitting,
    submitError,
  };
}
